/**
 * Pure placement math for wp:anchor images — where Word actually puts a
 * positioned anchor on the page. Two consumers:
 *
 *  - BODY canvas: non-floating anchors stay IN FLOW (so following text is
 *    pushed below them, Word's wrapSquare-with-no-room-beside behaviour)
 *    but get their Word x via margin-left and, for pulled-up anchors, their
 *    Word y via a clamped negative margin-top. Full-page banners bleed into
 *    the horizontal margins exactly like Word instead of clamping to the
 *    text column.
 *
 *  - CHROME (header/footer): anchors are extracted from the part's flow and
 *    absolutely positioned on the page card per posH/posV — headers hold
 *    page-wide banners positioned relative to the PAGE, which the header
 *    text box (margin-inset, height-clamped) cannot represent.
 *
 * Word clamps anchors into the page; both helpers reproduce that.
 * All px are document px (96dpi), sections are the mm geometry payload.
 */

import { mmToPx } from './paginator.js'

/**
 * A body anchor the flow model cannot place: page-relative VERTICAL
 * positioning is an absolute page coordinate (e.g. the MAATUKK page-bottom
 * wave), so these render as zero-footprint ghosts in the editor and the
 * pagination controller projects them onto their page card like chrome.
 * wrapNone/behindDoc anchors keep the absolute-in-paragraph path.
 */
export function isPageProjected(info) {
  if (!info || info.kind === 'none' || info.behindDoc) return false
  const posV = info.posV ?? {}
  return (
    posV.relativeFrom === 'page' &&
    ((posV.offsetPx !== null && posV.offsetPx !== undefined) || Boolean(posV.align))
  )
}

/**
 * Card-relative {leftPx, topPx, widthPx} for a chrome-painted anchor.
 * widthPx is capped so the image never paints past the right page edge —
 * Word clips at the page; we scale (height rides along via height:auto),
 * which reads as "fits the page width".
 *
 * @param {{widthPx: number, heightPx: number, posH: object, posV: object}} anchor
 * @param {{pageWidthMm: number, pageHeightMm: number, marginTopMm: number,
 *          marginRightMm: number, marginBottomMm: number, marginLeftMm: number}} section
 */
export function chromeAnchorPlacement(anchor, section) {
  const pageW = mmToPx(section.pageWidthMm)
  const pageH = mmToPx(section.pageHeightMm)
  const mL = mmToPx(section.marginLeftMm)
  const mR = mmToPx(section.marginRightMm)
  const mT = mmToPx(section.marginTopMm)
  const mB = mmToPx(section.marginBottomMm)
  const w = anchor.widthPx || 0
  const h = anchor.heightPx || 0
  const posH = anchor.posH ?? {}
  const posV = anchor.posV ?? {}

  let left
  if (posH.offsetPx !== null && posH.offsetPx !== undefined) {
    left = (posH.relativeFrom === 'page' ? 0 : mL) + posH.offsetPx
  } else if (posH.align === 'center') {
    left = posH.relativeFrom === 'page' ? (pageW - w) / 2 : mL + (pageW - mL - mR - w) / 2
  } else if (posH.align === 'right') {
    left = posH.relativeFrom === 'page' ? pageW - w : pageW - mR - w
  } else {
    left = posH.relativeFrom === 'page' ? 0 : mL
  }

  let top
  if (posV.offsetPx !== null && posV.offsetPx !== undefined) {
    top = (posV.relativeFrom === 'page' ? 0 : mT) + posV.offsetPx
  } else if (posV.align === 'center') {
    top = posV.relativeFrom === 'page' ? (pageH - h) / 2 : mT + (pageH - mT - mB - h) / 2
  } else if (posV.align === 'bottom') {
    top = posV.relativeFrom === 'page' ? pageH - h : pageH - mB - h
  } else {
    top = posV.relativeFrom === 'page' ? 0 : mT
  }

  const leftPx = Math.round(Math.max(0, Math.min(left, Math.max(0, pageW - w))))
  return {
    leftPx,
    topPx: Math.round(Math.max(0, Math.min(top, Math.max(0, pageH - h)))),
    widthPx: Math.round(w > 0 ? Math.min(w, pageW - leftPx) : 0),
  }
}

/**
 * In-flow horizontal placement for a non-floating BODY anchor: the
 * margin-left (document px, relative to the text column) that puts the
 * image at its Word x. Null when the anchor carries no usable posH — the
 * caller keeps the centered-block fallback.
 *
 * @returns {number|null}
 */
export function flowAnchorMarginLeftPx(info, widthPx, section) {
  if (!section) return null
  const posH = info?.posH ?? {}
  const hasOffset = posH.offsetPx !== null && posH.offsetPx !== undefined
  if (!hasOffset && !posH.align) return null

  const pageW = mmToPx(section.pageWidthMm)
  const mL = mmToPx(section.marginLeftMm)
  const mR = mmToPx(section.marginRightMm)
  const contentW = pageW - mL - mR
  const w = widthPx || 0

  let marginLeft
  if (hasOffset) {
    marginLeft = posH.relativeFrom === 'page' ? posH.offsetPx - mL : posH.offsetPx
  } else if (posH.align === 'center') {
    marginLeft = posH.relativeFrom === 'page' ? (pageW - w) / 2 - mL : (contentW - w) / 2
  } else if (posH.align === 'right') {
    marginLeft = posH.relativeFrom === 'page' ? pageW - w - mL : contentW - w
  } else {
    marginLeft = posH.relativeFrom === 'page' ? -mL : 0
  }

  // Word clamps into the page: never left of the page edge, and never
  // pushed past the right edge (a page-wide image pins to the left edge).
  marginLeft = Math.min(marginLeft, pageW - mL - w)
  return Math.round(Math.max(marginLeft, -mL))
}

/**
 * Rendered width cap for an in-flow positioned body anchor: Word never
 * paints past the right page edge, so an over-wide image scales down to
 * fit (height rides along via height:auto). Null when the natural width
 * already fits.
 */
export function flowAnchorWidthPx(marginLeftPx, widthPx, section) {
  if (!section || !widthPx) return null
  const pageW = mmToPx(section.pageWidthMm)
  const mL = mmToPx(section.marginLeftMm)
  const available = pageW - mL - marginLeftPx
  return widthPx > available ? Math.round(Math.max(0, available)) : null
}

/**
 * In-flow vertical pull-up for a BODY anchor positioned above its flow
 * line (negative margin/page-relative posV offset): a negative margin-top,
 * clamped so the image never rises above the page top — which reproduces
 * Word's own clamp, since the flow position is the content top. Null when
 * the anchor doesn't pull up (in-flow y is already Word's y, bar wrap
 * subtleties).
 *
 * @returns {number|null}
 */
export function flowAnchorMarginTopPx(info, section) {
  if (!section) return null
  const posV = info?.posV ?? {}
  if (posV.offsetPx === null || posV.offsetPx === undefined) return null

  const mT = mmToPx(section.marginTopMm)
  const pull = posV.relativeFrom === 'page' ? posV.offsetPx - mT : posV.offsetPx
  if (pull >= 0) return null
  return Math.round(Math.max(pull, -mT))
}
